import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HotToastService } from '@ngneat/hot-toast';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';

import { CountryDto, TestSessionForRegistrationDto } from 'src/app/core/dtos/dto';
import { BrandEnum, TestPurposeEnum, TestTypeEnum } from 'src/app/core/dtos/enum';
import { AffiliateService } from 'src/app/core/services/affiliate.service';
import { CountryRegionService } from 'src/app/core/services/country-region.service';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { LoadingService } from 'src/app/core/services/loading.service';
import { RegistrationService } from 'src/app/core/services/registration.service';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';
import { selectBusinessAccountParentKey } from 'src/app/state/business-account-parent-key.selector';
import { selectIsBusinessAccount } from 'src/app/state/my-account.selectors';
import { setRegTestSession } from 'src/app/state/reg-data.actions';
import { selectRegData } from 'src/app/state/reg-data.selectors';

@Component({
  selector: 'app-sitting-selection-web-delivery',
  templateUrl: './sitting-selection-web-delivery.component.html',
  styleUrls: ['./sitting-selection-web-delivery.component.scss']
})
export class SittingSelectionWebDeliveryComponent implements OnInit {

  @ViewChild('startingModal') startingModal: ModalComponent;

  availableSittings: TestSessionForRegistrationDto[];
  privateSittings: TestSessionForRegistrationDto[];
  publicSittings: TestSessionForRegistrationDto[];
  showPrivateSittings: boolean;
  showSelectSittingsTypeOption: boolean;
  startingModalTitle: string;
  startingModalBody: string;
  brandId: BrandEnum;
  brandName: string;
  testTypeId: number;
  testPurposeId: number;
  testTypeIdForCael = TestTypeEnum.CAEL2;
  testPuposeIdForCaelOnline = TestPurposeEnum.UniversityAdmissions;
  affiliateId: string;
  companyName: string;
  countryAbbr: string;
  parentAccountSignedIn: boolean;
  countries: CountryDto[];
  canDisplayError: boolean;

  constructor(
    private store: Store,
    private registrationService: RegistrationService,
    private configHelper: ConfigHelper,
    private toastService: HotToastService,
    private loadingService: LoadingService,
    private router: Router,
    private route: ActivatedRoute,
    private affiliateService: AffiliateService,
    private countryRegionService: CountryRegionService
  ) {
  }

  ngOnInit(): void {
    this.brandId = this.configHelper.getBrandId();
    this.brandName = this.configHelper.getBrandName();
    // when using the links from WordPress
    this.route.queryParams
      .subscribe(params => {
        if (params.affiliate) {
          this.affiliateId = params.affiliate;
          this.companyName = params.companyName;
          this.affiliateService.checkAndInitAffiliateTracking();
        }
        if (this.affiliateId === "" || this.affiliateId === undefined) {
          this.affiliateService.checkAndInitAffiliateTracking();
        }
        if (this.affiliateId) {
          this.affiliateService.logoutCurrentBusinessAccount();
        }
        if (this.brandId === BrandEnum.CAEL && params.siteCode && params.testTypeId) {
          this.router.navigate(['/registration/proof-of-identity'], { queryParams: { siteCode: params.siteCode, testTypeId: params.testTypeId } });
        }
        else {
          this.checkBusinessAccountAndRegInfo();
        }
      });
  }

  checkBusinessAccountAndRegInfo() {
    combineLatest([
      this.store.select(selectIsBusinessAccount),
      this.store.select(selectBusinessAccountParentKey),
      this.store.select(selectRegData)
    ]).pipe(take(1))
      .subscribe(
        result => {
          if (result[0]) {
            this.router.navigate(['/batch-registration/sitting-selection']);
          }
          else {
            this.parentAccountSignedIn = result[1]?.email ? true : false;
            if (this.brandId === BrandEnum.CAEL) {
              this.testTypeId = this.testTypeIdForCael;
              this.testPurposeId = this.testPuposeIdForCaelOnline;
            }
            else {
              this.testTypeId = result[2]?.testSession?.testTypeId;
              this.testPurposeId = result[2]?.selectedTestPurposeOption?.testPurposeId;
              if (!(this.testTypeId && this.testPurposeId)) {
                if (this.affiliateId) {
                  this.router.navigate(['registration/test-selection-web-delivery'], { queryParams: { affiliate: this.affiliateId, companyName: this.companyName } });
                }
              }
            }
            if (this.parentAccountSignedIn) {
              this.getCountrie();
            }
            else {
              this.getAllOnlineSittings();
            }
          }
        },
        () => {
          this.toastService.warning("Unable to load sittings.");
        });
  }

  displayModal() {
    const identityDocumentCheck = this.brandId === BrandEnum.CAEL ? 'https://www.cael.ca/take-cael/accepted-identification/' : "https://www.celpip.ca/take-celpip/test-day-information/";
    this.startingModalBody = `Before beginning your ${this.brandName} Online registration, ensure that you have your <a href = ${identityDocumentCheck} target="_blank"> acceptable identity document </a>
    and an accepted form of payment on hand, as they are required to complete the registration. Please also be aware that you will be required to take a photo of yourself during the registration.`;
    this.startingModalTitle = "Important Note";
    this.startingModal.open();
  }

  getAllOnlineSittings() {
    this.availableSittings = [];
    this.canDisplayError = false;
    this.loadingService.show();
    this.registrationService.getWebDeliveryTestSessions(this.testPurposeId, this.brandId, this.testTypeId, this.parentAccountSignedIn, this.countryAbbr)
      .subscribe(
        result => {
          this.loadingService.hide();
          this.canDisplayError = true;
          if (result.content.errorMsg === "NotAvailable") {
            this.toastService.warning(`${this.brandName} Online is not currently available in your region`);
          }
          else if (result.content.errorMsg === "RegionalAccessDenied") {
            this.toastService.warning("The test sitting you have selected is not available in your region. Please be sure to select a test sitting within your own country. Click <a href='https://www.cael.ca/search-test-results/?testType=CAEL2'>here</a> to find more test sittings.");
          }
          else {
            this.availableSittings = result.content.availableTestSessions;
            if (this.availableSittings.find(x => x.isPrivate === true)) {
              this.showPrivateSittings = true;
              this.showSelectSittingsTypeOption = true;
            }
            this.privateSittings = this.availableSittings.filter(x => x.isPrivate === true);
            this.publicSittings = this.availableSittings.filter(x => x.isPrivate === false);
            if (this.privateSittings.length > 0 || this.publicSittings.length > 0) {
              this.displayModal();
            }
          }
        },
        () => {
          this.loadingService.hide();
          this.toastService.warning("Unable to load sittings.");
        });

  }

  finalizeSelectedSitting(sitting: TestSessionForRegistrationDto) {
    this.store.dispatch(setRegTestSession({ testSession: sitting }));
    this.router.navigate(['/registration/proof-of-identity']);
  }

  toggleTestType(value: boolean) {
    this.showPrivateSittings = value;
  }

  getCountrie() {
    this.countryRegionService.getCountriesWithActiveTestCentres()
      .subscribe(
        (result) => {
          this.countries = result.content;
        },
        () => {
          this.toastService.warning("Unable to load countries");
        }
      );
  }

}
